import { createContext, ReactNode, useEffect, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import moment from 'moment';
import { useSnackbar } from 'notistack';

import { taskAssignsApi } from '../../../axios';
import { mobileUsersApi } from '../../../axios/mobileUsers';
import { planningsApi } from '../../../axios/plannings';
import { useFeatureFlags, useLanguage } from '../../../hooks';
import { useUserDataStore } from '../../../newStore/userDataStore';
import { planningKeys, usePlanningQuery, usePlanningTypesQuery } from '../../../queries/plannings';
import { CreateTaskAssignBody, NameAndIdElement, NameAndIdElementTuple } from '../../../types';
import {
  CreatePlanningTaskAssignPayload,
  PlanningDetail,
  PlanningType,
} from '../../../types/plannings';
import { paths } from '../../../utils/navigation';
import {
  mapScheduledSendToOption,
  trackTaskCreated,
  trackTaskCreateFormOpened,
  trackTaskDetailViewed,
  trackTaskEdited,
} from '../taskAssignerAnalytics';
import { formatNotifications } from '../utils/editTaskHelpers';
import { createNotifString } from './addnotification';
import {
  initialState,
  TaskAssigned,
  TaskAssignerActions,
  TaskAssignerReducer,
} from './TaskAssignerReducer';

interface ImportAuditPrefillState {
  importAudit?: {
    auditLogId: number;
    entryId: number;
    rowNumber?: number;
  };
  rowData?: Record<string, string | number | null | undefined>;
}

function parseImportDate(raw: unknown): Date | null {
  if (raw == null || raw === '') return null;
  const str = String(raw).trim();
  // Try DD/MM/YYYY HH:mm first (the format used by the import template)
  const m = moment(str, ['DD/MM/YYYY HH:mm', 'DD/MM/YYYY', moment.ISO_8601], true);
  if (m.isValid()) return m.toDate();
  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? null : fallback;
}

export interface PlanningContextValue {
  planningId: number;
  formId: number;
  returnTo: string | null;
  excludeUserIds: number[];
  meterStrategyLevelId: number | null;
}

export const TaskAssignerContext = createContext<{
  task: TaskAssigned;
  dispatchTask: React.Dispatch<TaskAssignerActions>;
  formsList: NameAndIdElement[];
  saveTask: () => void;
  goBack: () => void;
  isSubmitting: boolean;
  confirmationModalOpen: boolean;
  setConfirmationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  editTask: () => void;
  editMode: boolean;
  // True when the form is hydrated from an existing GroupTaskAssign and the
  // submit should hit `PUT /task_assigner/groups/:id`. Mutually exclusive
  // with the standard individual `editMode`.
  groupEditMode: boolean;
  groupName?: string;
  // True once initial state is ready to consume. In create-mode it starts
  // true. In edit-mode it flips once `task_data_loaded` hydrates the form
  // (proxied by `task.forms.missing` going false). Consumers that need to
  // wait for hydration before applying defaults can gate on this latch.
  editDataLoaded: boolean;
  disableSaveButton?: boolean;
  accountId?: number;
  initialScheduledSendDaysBefore: number | null;
  planningContext: PlanningContextValue | null;
  planning: PlanningDetail | null;
  meterType: PlanningType | null;
  editingPlanningTask: boolean;
  /** `cycle_day` of the planning task being edited, or null while creating. */
  editingTaskCycleIndex: number | null;
}>(null);

export const TaskAssignerProvider = ({ children }: { children: ReactNode }) => {
  const lang = useLanguage();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Both edit modes coexist on the same provider:
  //   `taskId`              → individual edit (existing flow)
  //   `groupTaskAssignId`   → group edit (hydrates from the group detail
  //                            endpoint, persists via PUT on the group)
  const { taskId, groupTaskAssignId } = useParams();
  const groupEditMode = Boolean(groupTaskAssignId);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [formsList, setFormsList] = useState<NameAndIdElement[]>([]);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [task, dispatchTask] = useReducer(TaskAssignerReducer, initialState);
  const [initialScheduledSendDaysBefore, setInitialScheduledSendDaysBefore] = useState<
    number | null
  >(null);
  // Group name surfaced read-only in the assignee panel during group edit.
  const [groupName, setGroupName] = useState<string | undefined>(undefined);
  const [editingPlanningTask, setEditingPlanningTask] = useState(false);
  // `cycle_day` of the task being edited. The cycle preview needs it because the
  // backend re-anchors an edit on the row's own cycle, not on the live one.
  const [editingTaskCycleIndex, setEditingTaskCycleIndex] = useState<number | null>(null);
  const { checkFlag } = useFeatureFlags();
  const { currentUser } = useUserDataStore();
  const accountId = currentUser?.company_id;

  // When navigated here from the import audit "Fix & Retry" button, hydrate
  // scalar fields from the failed row and capture the entry id so we can
  // mark it as resolved after a successful save.
  const prefillState = (location.state ?? {}) as ImportAuditPrefillState;
  const importAuditMeta = prefillState.importAudit;

  // Planning handoff: any time `planningId` is in the URL the task is persisted
  // via the planning task-assign endpoint and the user is sent back to
  // `returnTo` afterwards. `formId` is set when the caller (wizard step 2)
  // already knows which form the task is for; from the planning detail page
  // the user picks the form themselves so it's absent.
  const planningIdParam = searchParams.get('planningId');
  const planningFormIdParam = searchParams.get('formId');
  const returnToParam = searchParams.get('returnTo');
  // 'group' when editing a planning group task (the edit id is a
  // GroupTaskAssign). Hydrates the groups field instead of users.
  const rowTypeParam = searchParams.get('rowType');
  // Blacklist of assignees already assigned to the same form of this planning.
  // The editor renders these ids disabled to prevent the user from creating
  // a (planning_id, form_id, mobile_user_id) collision. Built by the caller
  // (wizard step 2) when navigating to the editor — see Step2_Tasks#goEditTaskAssign.
  const excludeUserIdsParam = searchParams.get('excludeUserIds');
  const excludeUserIds: number[] = excludeUserIdsParam
    ? excludeUserIdsParam
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0)
    : [];
  // W4: meter strategy level when the template task is created from Step 2
  // of a meter planning. Absent in the temporal flow.
  const meterStrategyLevelIdParam = searchParams.get('meterStrategyLevelId');
  const parsedMeterLevelId = meterStrategyLevelIdParam ? Number(meterStrategyLevelIdParam) : NaN;
  const meterStrategyLevelId =
    Number.isFinite(parsedMeterLevelId) && parsedMeterLevelId > 0 ? parsedMeterLevelId : null;
  // Default location handed off from a planning's Step 2, used to pre-fill a
  // brand-new task's location (the user can still override it).
  const defaultLocationIdParam = searchParams.get('defaultLocationId');
  const parsedDefaultLocationId = defaultLocationIdParam ? Number(defaultLocationIdParam) : NaN;
  const defaultLocationId =
    Number.isFinite(parsedDefaultLocationId) && parsedDefaultLocationId > 0
      ? parsedDefaultLocationId
      : null;
  const defaultLocationType: 'NestableLocation' | 'Location' =
    searchParams.get('defaultLocationType') === 'NestableLocation' ? 'NestableLocation' : 'Location';
  const planningContext: PlanningContextValue | null = planningIdParam
    ? {
        planningId: Number(planningIdParam),
        formId: planningFormIdParam ? Number(planningFormIdParam) : 0,
        returnTo: returnToParam,
        excludeUserIds,
        meterStrategyLevelId,
      }
    : null;

  const { data: planningData, isLoading: planningLoading } = usePlanningQuery(
    planningContext?.planningId ?? null,
  );
  const planning = planningData ?? null;
  // The meter config (execution window, available duration) lives on the
  // type, not on the planning. We fetch it to derive the estimated dates and
  // the template task's duration_hours.
  const { data: planningTypes } = usePlanningTypesQuery({ enabled: planningContext != null });
  const meterType =
    planning?.planning_type_id != null
      ? planningTypes?.find((type) => type.id === planning.planning_type_id) ?? null
      : null;

  const refreshMetadataList = async () => {
    const { forms } = await taskAssignsApi.getLightFilterMetadata();
    const mapEntries = (entry: NameAndIdElementTuple) => ({
      label: entry[0],
      id: entry[1],
    });

    const mappedForms = forms.map((entry) => mapEntries(entry));
    setFormsList(mappedForms);
    return mappedForms;
  };

  const hydrateFromImportAudit = async (
    rowData: ImportAuditPrefillState['rowData'],
    forms: NameAndIdElement[],
  ) => {
    if (!rowData) return;

    const matchedForm = rowData.task
      ? forms.find((f) => f.label === String(rowData.task))
      : undefined;

    // The import worker resolves the user by MobileUser.identifier (typically
    // the email). The form's user picker uses /filter_metadata which only
    // exposes the display name -- not enough to match an emailed identifier.
    // Reuse the same activated-users endpoint (id + name + email) the rest
    // of the app already calls for assignee/notification pickers and match
    // the row's user column against email first, then name as a fallback.
    const rawUser = rowData.user == null ? '' : String(rowData.user).trim();
    const normalizedUser = rawUser.toLowerCase();
    let matchedUser: { id: number } | undefined;
    if (rawUser) {
      try {
        const activatedUsers = await mobileUsersApi.getAllActivatedMobileUsers();
        matchedUser =
          activatedUsers.find((u) => u.email?.trim().toLowerCase() === normalizedUser) ??
          activatedUsers.find((u) => u.name?.trim().toLowerCase() === normalizedUser);
      } catch (_error) {
        // Non-fatal: leave the user field empty so the user can pick manually.
      }
    }

    const parsedDate = parseImportDate(rowData.date);
    const gapNum = Number(rowData.gap);
    const mandatoryNum = Number(rowData.mandatory);
    const lateAllowedRaw = rowData.late_response_allowed;
    const lateAllowed = lateAllowedRaw === 1 || lateAllowedRaw === '1' || lateAllowedRaw === 'true';

    dispatchTask({
      type: 'task_data_loaded',
      payload: {
        ...initialState,
        forms: matchedForm
          ? { value: [matchedForm.id], showError: false, missing: false }
          : initialState.forms,
        users: matchedUser
          ? { value: [matchedUser.id], showError: false, missing: false }
          : initialState.users,
        location: initialState.location,
        locationType: {
          value: rowData.location_type === 'NestableLocation' ? 'NestableLocation' : 'Location',
          showError: false,
          missing: false,
        },
        description: {
          value: rowData.description ? String(rowData.description) : '',
          showError: false,
          missing: false,
        },
        gap: {
          value: Number.isFinite(gapNum) && gapNum > 0 ? gapNum : initialState.gap.value,
          showError: false,
          missing: false,
        },
        subTasks: { value: [], showError: false, missing: false },
        dates: parsedDate
          ? { value: [parsedDate], showError: false, missing: false }
          : initialState.dates,
        hour: parsedDate
          ? { value: parsedDate, showError: false, missing: false }
          : initialState.hour,
        notifications: { value: [], showError: false, missing: false },
        required: {
          value: (mandatoryNum === 0 || mandatoryNum === 2 ? mandatoryNum : 2) as 0 | 2,
          showError: false,
          missing: false,
        },
        canSubmitLate: { value: lateAllowed, showError: false, missing: false },
        taskRecurrence: initialState.taskRecurrence,
      },
    });
  };

  function parseDateKeepingTimezone(dateString: string) {
    const dateWithoutTimezone = moment
      .utc(dateString.substring(0, 23))
      .format('M/DD/yy, hh:mm:ss a')
      .toUpperCase();
    return new Date(dateWithoutTimezone);
  }

  const refreshEditInfo = async () => {
    try {
      const data = await taskAssignsApi.getEditInfo(parseInt(taskId), rowTypeParam ?? undefined);
      // Planning group task: the id is a GroupTaskAssign, so hydrate the groups
      // field (read-only in the editor) instead of users. The group itself is
      // changed via Reassign, not here.
      const isGroupRow = data.row_type === 'group';
      if (isGroupRow) setGroupName(data.group_name ?? '');
      dispatchTask({
        type: 'task_data_loaded',
        payload: {
          users: {
            value: isGroupRow ? [] : [data.task.mobile_user_id],
            showError: false,
            missing: false,
          },
          // Individual edit targets a single TaskAssign row; group edit
          // hydrates the destination group. The drawer hides the Groups tab in
          // edit mode (see TaskInfoContainer showGroupsTab gating).
          // Guard `group_id`: a group row whose id failed to resolve must not
          // leak `[undefined]` into the editor state (id comparisons would
          // treat it as a real selection). Fall back to an empty selection.
          groups: {
            value: isGroupRow && data.group_id != null ? [data.group_id] : [],
            showError: false,
            missing: false,
          },
          forms: { value: [data.task.form_id], showError: false, missing: false },
          location: { value: data.task.location_id, showError: false, missing: false },
          locationType: {
            value: data.task.location_type || 'Location',
            showError: false,
            missing: false,
          },
          description: { value: data.task.description, showError: false, missing: false },
          gap: { value: data.task.gap, showError: false, missing: false },
          subTasks: {
            value: data.subtasks.map((subtask: any) => subtask.name),
            showError: false,
            missing: false,
          },
          dates: {
            value: [new Date(data.task.start_time)],
            showError: false,
            missing: false,
          },
          hour: {
            value: parseDateKeepingTimezone(data.task.start_time),
            showError: false,
            missing: false,
          },
          // Planning tasks: the editor shows cycle day/time selectors (not
          // the date picker). The backend returns `day_in_cycle` /
          // `time_of_day` decoded from the absolute `start_time` (anchored at
          // `planning.start_date`) so these selectors load the real value
          // instead of the default. Absent for non-planning tasks (the
          // regular editor ignores them). In daily, `day_in_cycle` is the hour.
          cycleDay: {
            value: data.day_in_cycle ?? 1,
            showError: false,
            missing: false,
          },
          timeOfDay: {
            value: data.time_of_day ?? '09:00',
            showError: false,
            missing: false,
          },
          notifications: {
            value: data.task.notifications ? formatNotifications(data.task.notifications) : [],
            showError: false,
            missing: false,
          },
          required: {
            value:
              data.task.mandatory === 'for_everyone'
                ? 2
                : data.task.mandatory === 'for_one'
                ? 1
                : 0,
            showError: false,
            missing: false,
          },
          canSubmitLate: {
            value: data.task.late_response_allowed,
            showError: false,
            missing: false,
          },
          asset: {
            value: data.asset_id ? { id: data.asset_id, name: data.asset_name || '' } : null,
            showError: false,
            missing: false,
          },
          scheduledSendDaysBefore: {
            value: data.task.scheduled_send_days_before ?? null,
            showError: false,
            missing: false,
          },
        },
      });
      setInitialScheduledSendDaysBefore(data.task.scheduled_send_days_before ?? null);
      setEditingPlanningTask(data.task.planning_id != null);
      setEditingTaskCycleIndex(data.task.planning_id != null ? data.cycle_day ?? null : null);

      if (accountId) {
        trackTaskDetailViewed({
          accountId,
          taskStatus: data.task.acceptance_status ?? 'sent',
          scheduledSendDaysBefore: data.task.scheduled_send_days_before ?? null,
        });
      }
    } catch (error) {
      navigate(paths.taskAssigner.index);
      enqueueSnackbar(lang.task_assigner.task_does_not_exist, {
        variant: 'error',
      });
    }
  };

  // Hydrates the form from a GroupTaskAssign detail payload. Mirrors
  // `refreshEditInfo` but talks to the group endpoint and keeps `users`
  // empty (the destination group is locked, surfaced via `groupName`).
  const refreshGroupEditInfo = async () => {
    try {
      const data = await taskAssignsApi.getGroupTaskAssignDetail(parseInt(groupTaskAssignId!, 10));
      setGroupName(data.group.name);
      const startTimeDate = new Date(data.start_time);
      const requiredValue =
        data.mandatory === 'for_everyone' ? 2 : data.mandatory === 'for_one' ? 1 : 0;
      dispatchTask({
        type: 'task_data_loaded',
        payload: {
          // Group edit keeps the user list empty — the assignee panel
          // displays the locked group instead (see TaskInfoContainer).
          users: { value: [], showError: false, missing: false },
          groups: { value: [data.group.id], showError: false, missing: false },
          forms: { value: [data.form.id], showError: false, missing: false },
          location: { value: data.location_id ?? 0, showError: false, missing: false },
          locationType: {
            value: data.location_type ?? 'Location',
            showError: false,
            missing: false,
          },
          description: { value: data.description ?? '', showError: false, missing: false },
          gap: { value: data.gap, showError: false, missing: false },
          // Subtasks are propagated identically to every child at creation
          // time, so the group detail endpoint exposes the set read from
          // the first child. Empty array for legacy groups.
          subTasks: { value: data.subtasks ?? [], showError: false, missing: false },
          dates: { value: [startTimeDate], showError: false, missing: false },
          hour: { value: startTimeDate, showError: false, missing: false },
          // Reminder template is propagated to every pending child at save
          // time, so the parent's string is the supervisor source of truth.
          notifications: {
            value: data.notifications ? formatNotifications(data.notifications) : [],
            showError: false,
            missing: false,
          },
          required: {
            value: requiredValue as 0 | 1 | 2,
            showError: false,
            missing: false,
          },
          canSubmitLate: {
            value: data.late_response_allowed,
            showError: false,
            missing: false,
          },
          // Hydrate the pinned asset from the detail when present so the
          // edit form does not overwrite a previously saved asset with
          // null on the next submit. The shape matches the individual
          // flow above (id + best-effort name).
          asset: {
            value: data.asset_id ? { id: data.asset_id, name: data.asset_name ?? '' } : null,
            showError: false,
            missing: false,
          },
          scheduledSendDaysBefore: {
            value: null,
            showError: false,
            missing: false,
          },
        },
      });
    } catch (error) {
      navigate(paths.taskAssigner.index);
      enqueueSnackbar(lang.task_assigner.task_does_not_exist, { variant: 'error' });
    }
  };

  useEffect(() => {
    (async () => {
      const forms = await refreshMetadataList();
      if (taskId) {
        refreshEditInfo();
      } else if (groupTaskAssignId) {
        refreshGroupEditInfo();
      } else if (prefillState.rowData) {
        await hydrateFromImportAudit(prefillState.rowData, forms);
      } else if (planningContext?.formId) {
        dispatchTask({
          type: 'value_changed',
          payload: { key: 'forms', value: [planningContext.formId] },
        });
      }
    })();
  }, []);

  // Pre-fill the asset from the planning when creating a task inside a planning
  // that has one. Lives in its own effect (not the mount effect above) because
  // `planning` is fetched asynchronously via usePlanningQuery and is still null
  // at mount. The asset input is rendered locked downstream, so a one-time
  // prefill is enough — the ref guards against re-dispatching on re-renders.
  // Skipped in edit mode (`taskId`), where refreshEditInfo hydrates the asset.
  const assetPrefilledRef = useRef(false);
  useEffect(() => {
    if (taskId || assetPrefilledRef.current) return;
    const planningAsset = planning?.asset;
    if (!planningAsset) return;
    assetPrefilledRef.current = true;
    dispatchTask({
      type: 'value_changed',
      payload: {
        key: 'asset',
        value: {
          id: planningAsset.id,
          name: planningAsset.name,
          code: planningAsset.code ?? undefined,
        },
      },
    });
  }, [taskId, planning]);

  // Pre-fill a new task's location. Prefer the planning asset's own location
  // (auto-filled alongside the asset), falling back to the default location
  // handed off from the wizard's Step 1 (the no-asset case). One-time,
  // create-mode only; the field stays editable so the user can override it per
  // task. Mirrors the asset prefill above; waits for the planning to load so the
  // asset's location wins over the query-param default.
  const locationPrefilledRef = useRef(false);
  useEffect(() => {
    if (taskId || groupTaskAssignId || locationPrefilledRef.current) return;
    if (planningContext && planningLoading) return;

    const assetLocationId = planning?.asset?.location_id ?? null;
    const locationId = assetLocationId ?? defaultLocationId;
    if (locationId == null) return;
    // The asset's location is always a NestableLocation; otherwise trust the
    // type carried alongside the wizard default.
    const locationType: 'NestableLocation' | 'Location' =
      assetLocationId != null ? 'NestableLocation' : defaultLocationType;

    locationPrefilledRef.current = true;
    dispatchTask({ type: 'value_changed', payload: { key: 'location', value: locationId } });
    dispatchTask({ type: 'value_changed', payload: { key: 'locationType', value: locationType } });
  }, [
    taskId,
    groupTaskAssignId,
    planningLoading,
    planning,
    defaultLocationId,
    defaultLocationType,
  ]);

  // Amplitude: emit `Task Create Form Opened` once per mount in create mode.
  useEffect(() => {
    if (taskId) return;
    if (!accountId) return;
    trackTaskCreateFormOpened({ accountId, userId: currentUser?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, taskId]);

  const goBack = () => {
    if (planningContext?.returnTo) {
      return navigate(planningContext.returnTo);
    }
    if (importAuditMeta) {
      // Return to the audit log detail so the user sees the resolved entry.
      return navigate(
        paths.auditLogs.taskImportsDetail.replace(
          ':auditId',
          importAuditMeta.auditLogId.toString(),
        ),
      );
    }
    const filters = searchParams.get('filters');
    const page = searchParams.get('page');
    if (filters || page) {
      const redirectParams = new URLSearchParams({ filters, page }).toString();
      return navigate(`${paths.taskAssigner.index}?${redirectParams}`);
    }
    return navigate(paths.taskAssigner.index);
  };

  const parseTask = (task: TaskAssigned): CreateTaskAssignBody => {
    const formattedTask: CreateTaskAssignBody['task_assign'] = {
      location_id: task.location.value,
      location_type: task.locationType?.value,
      start_time: task.dates.value.map((date) => format(date, 'dd/MM/yyyy')).join(','),
      description: task.description.value,
      gap: task.gap.value.toString(),
      form_id: task.forms.value[0],
      mobile_user_id: task.users.value[0],
      late_response_allowed: task.canSubmitLate.value,
      mandatory: task.required.value,
      scheduled_send_days_before: checkFlag('ScheduledTaskSend')
        ? task.scheduledSendDaysBefore?.value ?? null
        : undefined,
      notifications:
        task.notifications.value.length > 0 ? createNotifString(task.notifications.value) : null,
      asset_id: task.asset?.value?.id ?? null,
    };
    const formattedRecurrence: CreateTaskAssignBody['task_recurrence'] = {
      frequency_unit: task.taskRecurrence?.value?.frequency_unit || 'none',
      days_of_week: task.taskRecurrence?.value?.days_of_week || [],
      minute: task.taskRecurrence?.value?.minute || 0,
      hour: task.taskRecurrence?.value?.hour || 0,
      day_of_month: task.taskRecurrence?.value?.day_of_month || 0,
      month: task.taskRecurrence?.value?.month || 0,
      frequency_value: task.taskRecurrence?.value?.frequency_value || 0,
      end_type: task.taskRecurrence?.value?.end_type || 'never',
      end_date: task.taskRecurrence?.value?.end_date || '',
      end_count: task.taskRecurrence?.value?.end_count || 0,
    };

    const groupTaskAssignEnabled = checkFlag('GroupTaskAssign');
    const hasGroups = groupTaskAssignEnabled && (task.groups?.value?.length ?? 0) > 0;

    return {
      task_assign: formattedTask,
      task_recurrence: checkFlag('TaskRecurrence') ? formattedRecurrence : null,
      // Mutually exclusive: when dispatching to groups we send `group_ids` and
      // an empty `mobile_users_ids` so the controller takes the group branch.
      // When the FF is off (or no groups selected), behavior is identical to
      // before this feature shipped.
      mobile_users_ids: hasGroups ? [] : task.users.value,
      task_forms_ids: task.forms.value,
      ...(hasGroups ? { group_ids: task.groups.value } : {}),
      'todo-value': task.subTasks.value.join(','),
      time: {
        date: task.hour.value.getHours() + ':' + task.hour.value.getMinutes(),
      },
    };
  };

  // W4: for a meter template task the available duration comes from the
  // type's template (the user does not pick it). The backend materializes it
  // per milestone, so deriving a reasonable `duration_hours` is enough.
  const meterDurationHours = (): number => {
    const value = meterType?.available_duration_value;
    const unit = meterType?.available_duration_unit;
    if (typeof value === 'number' && value > 0) {
      return unit === 'weeks' ? value * 7 * 24 : value * 24;
    }
    return task.gap.value && task.gap.value > 0 ? task.gap.value : 24;
  };

  const buildPlanningTaskAssignPayload = (
    planningId: number,
    formId: number,
    // Either a user or a group, never both (the selector is mutually
    // exclusive). A group payload makes the backend create a cycle-0
    // GroupTaskAssign template + children instead of an individual task.
    assignee: { mobileUserId?: number; groupId?: number },
  ): CreatePlanningTaskAssignPayload => ({
    planning_id: planningId,
    form_id: formId,
    // Emit exactly one assignee key, and only when its id actually resolved.
    // Spreading `{ mobile_user_id: assignee.mobileUserId }` unconditionally
    // would send `mobile_user_id: undefined` when neither id is present, which
    // on an edit can unset the assignee. Omitting the key leaves it unchanged.
    ...(assignee.groupId != null
      ? { group_id: assignee.groupId }
      : assignee.mobileUserId != null
      ? { mobile_user_id: assignee.mobileUserId }
      : {}),
    location_id: task.location.value || null,
    location_type: task.location.value ? task.locationType?.value ?? 'Location' : null,
    description: task.description.value || null,
    // `cycle_day` is the cycle *index* (0 = template).
    cycle_day: 0,
    // Meter template: anchor the cycle-0 template to its strategy level so the
    // backend stores `meter_strategy_level_id` (NULL milestone = template) and
    // Step 2 matches coverage by (level, form). Omitted for temporal plannings.
    ...(planningContext?.meterStrategyLevelId != null
      ? { meter_strategy_level_id: planningContext.meterStrategyLevelId }
      : {}),
    day_in_cycle: task.cycleDay?.value ?? 1,
    ...(planning?.frequency && planning.frequency !== 'daily'
      ? { time_of_day: task.timeOfDay?.value }
      : {}),
    // Meter template: the gap input is hidden, so derive the duration from the
    // meter type's available-duration config instead of the (stale) gap field.
    duration_hours:
      planningContext?.meterStrategyLevelId != null ? meterDurationHours() : task.gap.value,
    ...(checkFlag('ScheduledTaskSend')
      ? { scheduled_send_days_before: task.scheduledSendDaysBefore?.value ?? null }
      : {}),
    subtasks: (task.subTasks?.value ?? []).map((s) => s.trim()).filter(Boolean),
    late_response_allowed: task.canSubmitLate.value,
    asset_id: task.asset?.value?.id ?? null,
    mandatory: task.required.value,
    notifications:
      task.notifications.value.length > 0 ? createNotifString(task.notifications.value) : null,
  });

  const saveTask = async () => {
    setIsSubmitting(true);
    try {
      if (planningContext) {
        const formId = planningContext.formId || task.forms.value[0];
        // Assignees are mutually exclusive: groups OR individual users. With
        // groups, one createPlanningTaskAssign per group (the backend builds a
        // cycle-0 GroupTaskAssign template + children). With users, one per
        // user (same metadata each). Sequential iteration respects the backend
        // assign_id sequencer (`TaskAssignIndex.next_for`) and avoids races.
        const groupIds = task.groups.value;
        if (groupIds.length > 0) {
          for (const groupId of groupIds) {
            // eslint-disable-next-line no-await-in-loop
            await planningsApi.createPlanningTaskAssign(
              buildPlanningTaskAssignPayload(planningContext.planningId, formId, { groupId }),
            );
          }
        } else {
          const userIds = task.users.value.length > 0 ? task.users.value : [null];
          for (const userId of userIds) {
            if (userId == null) continue;
            // eslint-disable-next-line no-await-in-loop
            await planningsApi.createPlanningTaskAssign(
              buildPlanningTaskAssignPayload(planningContext.planningId, formId, {
                mobileUserId: userId,
              }),
            );
          }
        }
        // Drop the cached planning detail so the wizard's usePlanningQuery
        // refetches on remount and the new task_assign shows up as submitted.
        // The global staleTime is 2 min, so without this the stale (empty)
        // cache wins and the row stays as "Crear tarea". `detailFor` matches
        // both variants (with and without embedded task_assigns) and the
        // paginated list.
        await queryClient.invalidateQueries({
          queryKey: planningKeys.detailFor(planningContext.planningId),
        });
        await queryClient.invalidateQueries({
          queryKey: planningKeys.taskAssigns(planningContext.planningId),
        });
        setConfirmationModalOpen(false);
        enqueueSnackbar(lang.task_assigner.success_message, {
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'top' },
          autoHideDuration: 4000,
        });
        // Clear the submitting flag before the deferred navigation so the
        // button doesn't stay spinning during the 300ms window (mirrors the
        // non-planning paths that reset it in their finally block).
        setIsSubmitting(false);
        setTimeout(() => goBack(), 300);
        return;
      }

      const parsedTask = parseTask(task);
      const createResponse = await taskAssignsApi.createTaskAssign(parsedTask);

      if (importAuditMeta) {
        // Best-effort: tell the audit log this entry was fixed manually. We
        // don't fail the whole flow if this call errors out; the TaskAssign
        // was already created.
        try {
          await taskAssignsApi.resolveImportAuditEntry(
            importAuditMeta.auditLogId,
            importAuditMeta.entryId,
            createResponse?.task_assign_id,
          );
          queryClient.invalidateQueries({
            queryKey: ['importAudit', importAuditMeta.auditLogId],
          });
        } catch (resolveError) {
          // Log only; the user already saw the success message for the
          // TaskAssign creation.
          // eslint-disable-next-line no-console
          console.warn('Failed to mark import audit entry as resolved', resolveError);
        }
      }

      if (accountId) {
        const daysBefore = task.scheduledSendDaysBefore?.value ?? null;
        const participationType = task.required?.value === 2 ? 'mandatory' : 'voluntary';
        const hasReminders = task.notifications.value.length > 0;
        const timeToCompleteMinutes = (task.gap.value ?? 0) * 60;
        trackTaskCreated({
          accountId,
          userId: currentUser?.id,
          scheduledSendEnabled: checkFlag('ScheduledTaskSend'),
          scheduledSendOption: mapScheduledSendToOption(daysBefore),
          scheduledSendDaysBefore: daysBefore,
          participationType,
          hasReminders,
          timeToCompleteMinutes,
        });
      }
      setConfirmationModalOpen(false);
      enqueueSnackbar(lang.task_assigner.success_message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        autoHideDuration: 6000,
      });
      setTimeout(() => {
        goBack();
      }, 300);
    } catch (showError) {
      setIsSubmitting(false);
      enqueueSnackbar(lang.task_assigner.our_problem, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    }
  };

  const editGroupTask = async () => {
    setIsSubmitting(true);
    try {
      // Build the group payload from the same form state. `group_id` is
      // intentionally omitted — the destination group is immutable after
      // creation. `form_id` IS editable; children without a form_answer
      // adopt it, children that already responded stay frozen.
      // Clone so setHours/setMinutes don't mutate the date held in reducer state.
      const startTime = new Date(task.dates.value[0]);
      startTime.setHours(task.hour.value.getHours());
      startTime.setMinutes(task.hour.value.getMinutes());
      // Same wire format the create flow uses (createNotifString). Empty
      // string instead of null so the backend treats it as "wipe" and
      // clears the parent template + pending children schedules.
      const notificationsPayload =
        task.notifications.value.length > 0 ? createNotifString(task.notifications.value) : '';

      await taskAssignsApi.updateGroupTaskAssign(parseInt(groupTaskAssignId!, 10), {
        group_task_assign: {
          form_id: task.forms.value[0],
          description: task.description.value,
          gap: task.gap.value,
          // Mirror the create path: when the location is cleared, send a null
          // type too instead of a stale 'Location', so the cleared state is
          // unambiguous on the backend.
          location_id: task.location.value || null,
          location_type: task.location.value ? task.locationType?.value ?? 'Location' : null,
          start_time: startTime.toISOString(),
          mandatory:
            task.required.value === 2
              ? 'for_everyone'
              : task.required.value === 1
              ? 'for_one'
              : 'for_nobody',
          late_response_allowed: task.canSubmitLate.value,
          asset_id: task.asset?.value?.id ?? null,
        },
        to_do_checks: task.subTasks.value,
        notifications: notificationsPayload,
      });
      setConfirmationModalOpen(false);
      enqueueSnackbar(lang.task_assigner.update_success_message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        autoHideDuration: 4000,
      });
      // Defer the back navigation one tick so the success snackbar paints
      // before the route unmounts this provider. Matches the create/edit flows.
      setTimeout(() => {
        goBack();
      }, 300);
    } catch (showError) {
      setIsSubmitting(false);
      enqueueSnackbar(lang.task_assigner.our_problem, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    }
  };

  const editTask = async () => {
    if (groupEditMode) return editGroupTask();
    setIsSubmitting(true);
    try {
      if (planningContext) {
        // Planning task edit: same semantics as creation. The day/time
        // travels as `day_in_cycle`/`time_of_day` and the backend translates
        // it into an absolute `start_time` anchored at `planning.start_date`.
        // Same payload as `saveTask` via `buildPlanningTaskAssignPayload`.
        const formId = planningContext.formId || task.forms.value[0];
        // Abort if neither a group nor a user resolved (e.g. a group row whose
        // `group_id` came back null left both selections empty). Without this
        // the payload would carry no assignee key and the backend could unset
        // the responsible on the edited task.
        if (task.groups.value.length === 0 && task.users.value.length === 0) {
          enqueueSnackbar(lang.task_assigner.our_problem, { variant: 'error' });
          setIsSubmitting(false);
          return;
        }
        // A group task keeps its destination group on edit (group change is
        // done via Reassign), so send group_id; otherwise the single user.
        const editAssignee =
          task.groups.value.length > 0
            ? { groupId: task.groups.value[0] }
            : { mobileUserId: task.users.value[0] };
        await planningsApi.updatePlanningTaskAssign(
          parseInt(taskId),
          buildPlanningTaskAssignPayload(planningContext.planningId, formId, editAssignee),
        );
      } else {
        const parsedTask = parseTask(task);
        await taskAssignsApi.updateTaskAssign(parseInt(taskId), parsedTask);
      }
      if (accountId) {
        const daysBefore = task.scheduledSendDaysBefore?.value ?? null;
        const changed = daysBefore !== initialScheduledSendDaysBefore;
        trackTaskEdited({
          accountId,
          scheduledSendChanged: changed,
          scheduledSendOptionBefore: changed
            ? mapScheduledSendToOption(initialScheduledSendDaysBefore)
            : null,
          scheduledSendOptionAfter: mapScheduledSendToOption(daysBefore),
        });
      }
      // Same reason as in `saveTask`: the planning_detail / taskAssigns
      // cache survives 2 min due to the global staleTime. Without invalidating,
      // returning to the wizard shows the stale assignee list until it expires.
      if (planningContext) {
        await queryClient.invalidateQueries({
          queryKey: planningKeys.detailFor(planningContext.planningId),
        });
        await queryClient.invalidateQueries({
          queryKey: planningKeys.taskAssigns(planningContext.planningId),
        });
      }
      setConfirmationModalOpen(false);
      enqueueSnackbar(lang.task_assigner.update_success_message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' },
        autoHideDuration: 4000,
      });
      setTimeout(() => {
        goBack();
      }, 300);
    } catch (showError) {
      setIsSubmitting(false);
      enqueueSnackbar(lang.task_assigner.our_problem, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    }
  };

  // Disable Save while a required field is missing, and while the planning is
  // still loading: `assigneeReadonly` (draft vs published) is derived from
  // `planning`, so submitting before it resolves could act on a stale/flickering
  // read-only state. Gate on `isLoading` (not `planning == null`) so a failed
  // fetch doesn't lock Save permanently.
  const disableSaveButton =
    Object.values(task).some((value) => value.missing) ||
    (planningContext != null && planningLoading);
  const editMode = Boolean(taskId);
  // Latch that flips once edit data has hydrated. Stays true after that —
  // children must not bounce back to "loading" if the user clears a field
  // (e.g. emptying the forms selection in the drawer) after first paint.
  const [editDataLoaded, setEditDataLoaded] = useState(!editMode && !groupEditMode);
  useEffect(() => {
    if ((editMode || groupEditMode) && !task?.forms?.missing) setEditDataLoaded(true);
  }, [editMode, groupEditMode, task?.forms?.missing]);

  return (
    <TaskAssignerContext.Provider
      value={{
        task,
        dispatchTask,
        formsList,
        saveTask,
        goBack,
        isSubmitting,
        confirmationModalOpen,
        setConfirmationModalOpen,
        editTask,
        editMode,
        groupEditMode,
        groupName,
        editDataLoaded,
        disableSaveButton,
        accountId,
        initialScheduledSendDaysBefore,
        planningContext,
        planning,
        meterType,
        editingPlanningTask,
        editingTaskCycleIndex,
      }}
    >
      {children}
    </TaskAssignerContext.Provider>
  );
};
